import React from 'react';
import cn from 'classnames';
import {FixedSizeList as List, ListOnItemsRenderedProps} from 'react-window';
import memoize from 'memoize-one';
import {makeStyles} from '@material-ui/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import {DefaultRow, TableHeaders, TableHeadersProps, WrappedRow} from './TableComponents';
import {useResizeDetector} from 'react-resize-detector';
import './StudioVirtualizedTable.scss';
import {keyBy} from 'lodash';

const ROW_SIZE = 35;

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'block',
    flex: 1,
  },
  table: {
    height: '100%',
    width: '100%',
  },
  list: {},
  thead: {},
  tbody: {
    width: '100%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    boxSizing: 'border-box',
    minWidth: '100%',
    width: '100%',
  },
  headerRow: {},
  cell: {
    display: 'block',
    flexGrow: 0,
    flexShrink: 0,
  },
  expandingCell: {
    flex: 1,
  },
  column: {},
}));

export type ColumnTableProps = {
  id: string;
  label?: string;
  numeric?: boolean;
  width?: number;
  sortable?: boolean;
};
export type ColumnTablePropMap = {[id: string]: ColumnTableProps};

export type RowTableDataProps = {
  columnDefObj: ColumnTablePropMap;
  columnDef: ColumnTableProps[];
  // columnDef: Readonly<ColumnTableProps[]>;
  columns?: React.ReactNode;
  items: any[];
  classes: any;
};
export type RowTableProps = {
  index: number;
  style: any;
  data: RowTableDataProps;
  onClick?: (item: any) => void;
};

/**
 * itemKey function for returning the key prop for an item.
 * see https://react-window.vercel.app/#/api/FixedSizeList  -> itemKey prop
 */
const itemKey = (index: number, data: any) =>
  data.items[index] ? data.items[index].id : undefined;

/**
 * wee define this here because I wanted to pass my  columns prop from App, and classes from ReactWindowTable
 * see https://react-window.vercel.app/#/api/FixedSizeList  -> itemData prop
 */
const createItemData = memoize(
  (columnDef, columnDefObj, data, classes, columns): RowTableDataProps => ({
    columnDef,
    columnDefObj,
    items: data,
    classes,
    columns,
  })
);

export type StudioVirtualizedTableLoadProps = {
  totalData?: number;
  loadMore?: () => void;
};

export type SortDirection = 'asc' | 'desc';

export type StudioTableSortProps = {
  sort?: string;
  sortDir?: SortDirection;
  onSort?(id: string): void;
};

export type StudioVirtualizedTableProps = {
  data: any[];
  columns: ColumnTableProps[];
  rowSize?: number;
  showHeaders?: boolean;
  headerRenderer?: (props: TableHeadersProps) => React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  onRowClick?(item: any): void;
  onItemsRendered?: (args: ListOnItemsRenderedProps) => void;
} & StudioTableSortProps &
  StudioVirtualizedTableLoadProps;

const StudioVirtualizedTable = ({
  data,
  columns,
  rowSize = ROW_SIZE,
  showHeaders = true,
  headerRenderer,
  children,
  className,
  totalData = 0,
  loadMore = () => {},
  onRowClick = () => {},
  onItemsRendered = () => {},
  ...rest
}: StudioVirtualizedTableProps) => {
  const classes = useStyles();
  // memoized data passed to the Row item renderer
  const columnMap = keyBy(columns, el => el.id);
  const itemData = createItemData(columns, columnMap, data, classes, children);
  const callLoadMore = totalData !== undefined;
  const {width = 0, height = 0, ref} = useResizeDetector({
    handleWidth: true,
    handleHeight: true,
  });

  const onItemsRenderedLocal = (args: ListOnItemsRenderedProps) => {
    if (callLoadMore) {
      const {visibleStopIndex, overscanStopIndex} = args;
      if (visibleStopIndex === overscanStopIndex && data.length < totalData) {
        loadMore();
      }
    }
    onItemsRendered(args);
  };

  const row = (props: any) =>
    children
      ? WrappedRow({...props, onClick: onRowClick})
      : DefaultRow({...props, onClick: onRowClick});

  const header = showHeaders ? (
    <TableHead className={classes.thead}>
      {headerRenderer ? (
        headerRenderer({classes, columns, ...rest})
      ) : (
        <TableHeaders classes={classes} columns={columns} {...rest} />
      )}
    </TableHead>
  ) : (
    <></>
  );

  return (
    <div className={classes.root}>
      <Table className={cn(classes.table, className)}>
        {header}
        <TableBody className={cn(classes.tbody, 'virtualized-table')}>
          <div ref={ref}>
            <List
              className={classes.list}
              height={height || 0}
              width={width || 0}
              itemCount={data.length + (callLoadMore && data.length < totalData ? 1 : 0)}
              itemSize={rowSize}
              itemKey={itemKey}
              itemData={itemData}
              onItemsRendered={onItemsRenderedLocal}
            >
              {row}
            </List>
          </div>
        </TableBody>
      </Table>
    </div>
  );
};

export default StudioVirtualizedTable;
